import { Controller } from "@hotwired/stimulus";
import MapResourceLoader from "../services/map_resource_loader.js";

export default class extends Controller {
  static values = {
    coordinates: { type: Array, default: [] },
    zoom: { type: Number, default: 6 },
    clustering: { type: Boolean, default: true },
    heatmap: { type: Array, default: [] },
    gaps: { type: Array, default: [] },
    accessToken: String
  };

  connect() {
    // Ensure the container has proper dimensions
    this.element.style.height = this.element.style.height || "400px";
    this.element.style.minHeight = "300px";
    
    // Start preloading Mapbox resources immediately for better UX
    try {
      MapResourceLoader.preload('mapbox');
    } catch (error) {
      // MapResourceLoader not available yet, will load resources on demand
    }
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeMap());
    } else {
      this.initializeMap();
    }
  }

  disconnect() {
    if (this.map) {
      this.map.remove();
    }
  }

  async initializeMap() {
    // Simple check for access token
    if (!this.accessTokenValue) {
      return;
    }

    try {
      // Dynamically load Mapbox resources if not already loaded
      await MapResourceLoader.loadMapbox();
      
      // Now mapboxgl should be available
      if (typeof mapboxgl === 'undefined') {
        return;
      }

      // Set access token
      mapboxgl.accessToken = this.accessTokenValue;
      
      // Calculate center from all available data (prioritize heatmap, then coordinates, then gaps)
      const center = this.calculateOptimalCenter();
      
      // Initialize map with optimal configuration
      this.map = new mapboxgl.Map({
        container: this.element,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: center,
        zoom: this.zoomValue
      });

      // Add navigation controls
      this.map.addControl(new mapboxgl.NavigationControl());

      // Add markers when map loads
      this.map.on('load', () => {
        this.addSimpleMarkers();
        
        // Add heatmap if data exists
        if (this.hasHeatmapValue && this.heatmapValue.length > 0) {
          this.addHeatmap();
        }

        // Add gap markers if data exists
        if (this.hasGapsValue && this.gapsValue.length > 0) {
          this.addGapMarkers();
        }

              // Fit map to show all data optimally
      this.fitMapToAllData();
    });

    } catch (error) {
      // Error initializing Mapbox
    }
  }

  addSimpleMarkers() {
    // Simple marker addition
    this.coordinatesValue.forEach((coord, index) => {
      const lat = coord.latitude || coord.lat;
      const lng = coord.longitude || coord.lng;
      
      if (lat && lng) {
        // Create simple marker
        const marker = new mapboxgl.Marker()
          .setLngLat([lng, lat])
          .addTo(this.map);

        // Add popup if there's a label or name
        if (coord.label || coord.name) {
          const popup = new mapboxgl.Popup({ offset: 25 })
            .setHTML(`<div><strong>${coord.label || coord.name}</strong></div>`);
          marker.setPopup(popup);
        }
      }
    });

    // Fit map to show all markers if we have coordinates
    if (this.coordinatesValue.length > 1) {
      this.fitToMarkers();
    }
  }

  fitToMarkers() {
    const coordinates = this.coordinatesValue
      .filter(coord => (coord.latitude || coord.lat) && (coord.longitude || coord.lng))
      .map(coord => [coord.longitude || coord.lng, coord.latitude || coord.lat]);

    if (coordinates.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      coordinates.forEach(coord => bounds.extend(coord));
      this.map.fitBounds(bounds, { padding: 50 });
    }
  }

  calculateOptimalCenter() {
    // Prioritize heatmap data for centering
    if (this.hasHeatmapValue && this.heatmapValue.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      this.heatmapValue.forEach(point => {
        const lng = point.lng || point.longitude;
        const lat = point.lat || point.latitude;
        if (lng && lat) {
          bounds.extend([lng, lat]);
        }
      });
      if (!bounds.isEmpty()) {
        return bounds.getCenter().toArray();
      }
    }

    // Fall back to coordinates
    if (this.coordinatesValue.length > 0) {
      const validCoords = this.coordinatesValue.filter(coord => {
        const lat = coord.latitude || coord.lat;
        const lng = coord.longitude || coord.lng;
        return lat && lng;
      });
      
      if (validCoords.length > 0) {
        const totalLat = validCoords.reduce((sum, coord) => sum + (coord.latitude || coord.lat), 0);
        const totalLng = validCoords.reduce((sum, coord) => sum + (coord.longitude || coord.lng), 0);
        return [totalLng / validCoords.length, totalLat / validCoords.length];
      }
    }

    // Fall back to gaps
    if (this.hasGapsValue && this.gapsValue.length > 0) {
      const validGaps = this.gapsValue.filter(gap => {
        const lat = gap.lat || gap.latitude;
        const lng = gap.lng || gap.longitude;
        return lat && lng;
      });
      
      if (validGaps.length > 0) {
        const totalLat = validGaps.reduce((sum, gap) => sum + (gap.lat || gap.latitude), 0);
        const totalLng = validGaps.reduce((sum, gap) => sum + (gap.lng || gap.longitude), 0);
        return [totalLng / validGaps.length, totalLat / validGaps.length];
      }
    }

    // Default center (Ghana)
    return [-0.2057, 5.5600];
  }

  fitMapToAllData() {
    const bounds = new mapboxgl.LngLatBounds();
    let hasData = false;

    // Add heatmap points to bounds
    if (this.hasHeatmapValue && this.heatmapValue.length > 0) {
      this.heatmapValue.forEach(point => {
        const lng = point.lng || point.longitude;
        const lat = point.lat || point.latitude;
        if (lng && lat) {
          bounds.extend([lng, lat]);
          hasData = true;
        }
      });
    }

    // Add coordinates to bounds
    if (this.coordinatesValue.length > 0) {
      this.coordinatesValue.forEach(coord => {
        const lat = coord.latitude || coord.lat;
        const lng = coord.longitude || coord.lng;
        if (lat && lng) {
          bounds.extend([lng, lat]);
          hasData = true;
        }
      });
    }

    // Add gaps to bounds
    if (this.hasGapsValue && this.gapsValue.length > 0) {
      this.gapsValue.forEach(gap => {
        const lat = gap.lat || gap.latitude;
        const lng = gap.lng || gap.longitude;
        if (lat && lng) {
          bounds.extend([lng, lat]);
          hasData = true;
        }
      });
    }

    // Fit bounds if we have data
    if (hasData && !bounds.isEmpty()) {
      this.map.fitBounds(bounds, { 
        padding: 80,
        maxZoom: 12,
        duration: 1000
      });
    }
  }

  addHeatmap() {
    if (!this.heatmapValue || this.heatmapValue.length === 0) return;

    // Create heatmap source
    this.map.addSource('heatmap', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: this.heatmapValue.map(point => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [point.lng, point.lat]
          },
          properties: {
            weight: point.weight || 1
          }
        }))
      }
    });

    // Add heatmap layer
    this.map.addLayer({
      id: 'heatmap-layer',
      type: 'heatmap',
      source: 'heatmap',
      paint: {
        'heatmap-weight': [
          'interpolate',
          ['linear'],
          ['get', 'weight'],
          0, 0,
          6, 1
        ],
        'heatmap-intensity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0, 1,
          9, 3
        ],
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0, 'rgba(0, 0, 255, 0)',
          0.2, 'rgb(0, 0, 255)',
          0.4, 'rgb(0, 255, 255)',
          0.6, 'rgb(0, 255, 0)',
          0.8, 'rgb(255, 255, 0)',
          1, 'rgb(255, 0, 0)'
        ],
        'heatmap-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0, 2,
          9, 20
        ],
        'heatmap-opacity': 0.8
      }
    });
  }

  addGapMarkers() {
    if (!this.gapsValue || this.gapsValue.length === 0) return;

    this.gapsValue.forEach((gap, index) => {
      const lat = gap.lat || gap.latitude;
      const lng = gap.lng || gap.longitude;
      
      if (lat && lng) {
        // Create gap marker (circle)
        const marker = new mapboxgl.Marker({
          color: '#f59e0b',
          element: this.createCircleElement()
        })
          .setLngLat([lng, lat])
          .addTo(this.map);

        // Add popup
        const popup = new mapboxgl.Popup({ offset: 25 })
          .setHTML(`<strong>Coverage Gap</strong><br>Lat: ${lat.toFixed(6)}<br>Lng: ${lng.toFixed(6)}`);
        marker.setPopup(popup);
      }
    });
  }

  createCircleElement() {
    const el = document.createElement('div');
    el.className = 'gap-marker';
    el.style.width = '20px';
    el.style.height = '20px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = '#f59e0b';
    el.style.border = '3px solid #ffffff';
    el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
    return el;
  }
}